import React from "react";

import { Link } from "react-router-dom";

import ShowFlash from "ui/ShowFlash";
import QueryStatsTable, {
  QueryStatsTableSortBy,
} from "features/Queries/QueryStatsTable";
import { useRoutes } from "utils/routes";

import QueryDataMissingHint from "../QueryDataMissingHint";
import { QueryTableQuery, useQueryTableQuery } from "./gql/Query.generated";

import { useQueryStringSort } from "utils/hooks";

interface Props {
  databaseId: string;
  startTs: number;
  endTs: number;
  statementTypes: Array<string>;
  searchTerm: null | string;
  compare: boolean;
  header?: React.ReactNode;
}

const FETCH_BATCH_SIZE = 100;

const QueryTable: React.FunctionComponent<Props> = ({
  databaseId,
  startTs,
  endTs,
  statementTypes,
  searchTerm,
  compare,
  header,
}) => {
  const [sortOpts, setSortOpts] = useQueryStringSort<QueryStatsTableSortBy>({
    param: "sort",
    sortBy: "pctOfTotal",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } = useQueryTableQuery({
    variables: {
      databaseId,
      startTs,
      endTs,
      statementTypes,
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
      compare,
    },
  });

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (prev, { fetchMoreResult }): QueryTableQuery => {
          const fetched = fetchMoreResult.getQueryStats;
          resolve(fetched.length > 0);
          if (!prev || !prev.getQueryStats) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getQueryStats: [...prev.getQueryStats, ...fetched],
          };
        },
      });
    });
  };

  const trackNoneFlash =
    data?.getPostgresSettingDetails?.resetValuePrettyNoDelimiter == "none" ? (
      <PgssTrackNoneFlash serverId={data.getServerDetails.humanId} />
    ) : null;

  const noRowsText = searchTerm ? (
    "No queries match your search."
  ) : (
    <QueryDataMissingHint databaseId={databaseId} />
  );

  return (
    <QueryStatsTable
      header={
        <>
          {header}
          {trackNoneFlash}
        </>
      }
      loading={loading && !data}
      error={!!error}
      noRowsText={noRowsText}
      databaseId={databaseId}
      serverId={data?.getServerDetails.humanId}
      queries={data?.getQueryStats ?? []}
      fetchMoreData={fetchMoreData}
      sortOpts={sortOpts}
      setSortOpts={setSortOpts}
      searchTerm={searchTerm}
      compare={compare}
    />
  );
};

const PgssTrackNoneFlash: React.FunctionComponent<{ serverId: string }> = ({
  serverId,
}) => {
  const { serverConfigSetting } = useRoutes();
  return (
    <ShowFlash
      level="alert"
      msg={
        <>
          The Postgres setting{" "}
          <Link to={serverConfigSetting(serverId, "pg_stat_statements.track")}>
            pg_stat_statements.track
          </Link>{" "}
          is set to "none" for this server. Query performance statistics are not
          tracked in <code>pg_stat_statements</code>, and so not available in
          pganalyze.
        </>
      }
    />
  );
};

export default QueryTable;
